/**
 * Painting saved highlights back onto a rendered answer.
 *
 * The stored anchor is the quoted text, so re-applying it means finding that
 * text again in the DOM. Done with a `Range` over text nodes rather than by
 * rewriting `innerHTML`: the answer contains KaTeX output and live React
 * figures, and replacing markup underneath them would detach both.
 */

import { useEffect } from "react";
import type { Highlight } from "../../shared/highlights.ts";

const MARK_CLASS = "reader-highlight";

export function useHighlights(
  root: React.RefObject<HTMLElement | null>,
  highlights: readonly Highlight[],
  enabled: boolean,
): void {
  useEffect(() => {
    const host = root.current;
    if (!host || !enabled) return;

    // Undo the previous pass before re-applying; otherwise editing one note
    // would double-wrap every other highlight.
    unpaint(host);
    for (const highlight of highlights) paint(host, highlight);

    return () => unpaint(host);
  }, [root, highlights, enabled]);
}

function unpaint(host: HTMLElement): void {
  for (const mark of [...host.querySelectorAll(`.${MARK_CLASS}`)]) {
    const parent = mark.parentNode;
    if (!parent) continue;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    mark.remove();
    parent.normalize();
  }
}

/**
 * Wrap the first occurrence of the quote.
 *
 * Whitespace is normalised for matching because the selection collapsed it,
 * while the DOM keeps the line breaks markdown produced.
 */
function paint(host: HTMLElement, highlight: Highlight): void {
  const needle = normalize(highlight.quote);
  if (needle.length < 3) return;

  const nodes = textNodes(host);
  let haystack = "";
  const offsets: { node: Text; start: number }[] = [];

  for (const node of nodes) {
    offsets.push({ node, start: haystack.length });
    haystack += normalize(node.nodeValue ?? "");
  }

  const at = haystack.indexOf(needle);
  if (at === -1) return;

  const range = document.createRange();
  const start = locate(offsets, at);
  const end = locate(offsets, at + needle.length);
  if (!start || !end) return;

  try {
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);

    const mark = document.createElement("mark");
    mark.className = MARK_CLASS;
    mark.dataset.highlightId = highlight.id;
    if (highlight.note) mark.dataset.note = highlight.note;
    range.surroundContents(mark);
  } catch {
    // `surroundContents` throws when the range straddles element boundaries —
    // a highlight spanning a paragraph break. Skipping is correct: a partial
    // wrap would corrupt the markup.
  }
}

function locate(
  offsets: readonly { node: Text; start: number }[],
  position: number,
): { node: Text; offset: number } | null {
  for (let i = offsets.length - 1; i >= 0; i--) {
    const entry = offsets[i]!;
    if (entry.start <= position) {
      const within = position - entry.start;
      const length = (entry.node.nodeValue ?? "").length;
      return { node: entry.node, offset: Math.min(within, length) };
    }
  }
  return null;
}

function textNodes(host: HTMLElement): Text[] {
  const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      // Never inside maths or a figure: their markup is generated and a mark
      // element in the middle of it breaks the layout.
      node.parentElement?.closest(".katex, figure, .eq-number")
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });

  const found: Text[] = [];
  let current = walker.nextNode();
  while (current) {
    found.push(current as Text);
    current = walker.nextNode();
  }
  return found;
}

const normalize = (text: string): string => text.replace(/\s+/g, " ");
